import type { ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  clearDriveScrollMarker,
  readDriveScrollMarker,
  resolveScrollRestoreTarget,
} from '../lib/driveScrollRestore';
import { type SideNavState, closeSideNav, toggleSideNav } from '../lib/sideNav';
import styles from './AppShell.module.css';
import { SideNavDrawer } from './SideNavDrawer';
import { SideTabRail } from './TabBar';

interface AppShellProps {
  children: ReactNode;
  /** The bottom tab bar (mobile) / side rail (desktop) content. Rendered
   *  outside .scrollArea so it stays pinned regardless of scroll position,
   *  matching the mockup's flex-shrink:0 tab bar sitting below the scrolling
   *  content div. Hidden entirely during onboarding (showTabs=false). */
  tabBar?: ReactNode;
  /** The onboarding overlay — rendered as an absolutely-positioned sibling
   *  covering the shell, matching the mockup's position:absolute; inset:0. */
  overlay?: ReactNode;
  /** LANE STORY-TOUR: the guided coach-tour overlay (components/CoachMark.tsx)
   *  — DELIBERATELY a separate slot from `overlay` above, not folded into it.
   *  `overlay` (the Onboarding wizard) intentionally suppresses the tab bar
   *  and the nav trigger (see showTrigger below) because there is nothing
   *  real to navigate to yet at that stage; the coach tour is the opposite
   *  case — it exists specifically to walk someone through REAL, currently
   *  usable navigation (the Refine tab, the mic button), so tabBar/navTrigger
   *  must stay fully visible and functional underneath it. Rendered above
   *  `tabBar` in paint order (and above it in z-index, see
   *  CoachMark.module.css) so it can highlight the tab bar itself. */
  coachMark?: ReactNode;
  /** LANE W-NAV — useAuth().signOut, threaded down from App.tsx so the
   *  upper-left drawer's Sign out row can reuse the SAME sign-out Settings.tsx
   *  already wires (POST /auth/logout, then a hard navigate to /goodbye).
   *  Omitted (undefined) during onboarding/pre-auth renders where App.tsx has
   *  no signed-in user yet — see the nav trigger's own guard below, which
   *  hides the trigger entirely rather than rendering a drawer with a Sign
   *  out row that has nothing to sign out of. */
  onSignOut?: () => Promise<void>;
}

/**
 * The phone-frame shell every screen renders inside. Mobile-first: below
 * 1024px this IS the viewport. At ≥1024px it becomes a centered column with
 * a side tab-rail, per the brief.
 *
 * LANE W-NAV (Jason, from a live screenshot's pink top-left annotation: "I
 * need upper left navigation on every page it can trigger the side nav bar
 * in from the left but we need something besides the bottom only nav"): the
 * hamburger trigger + SideNavDrawer live HERE, in the shell, rather than in
 * any individual page — this is the one place guaranteed to render for
 * EVERY route (Home/Refine/Profiles/Jobs/JobDetail/Track/Settings all mount
 * as `children` inside the SAME AppShell), so it is automatically present on
 * every page without each page needing its own copy. It renders as an
 * absolutely-positioned sibling of .scrollArea (same containing block as
 * `overlay`/the onboarding cover), sitting ABOVE the scrolling content but
 * BELOW nothing — no page's own header (Refine's kicker, Home's avatar+gear
 * row, Settings'/JobDetail's inline back-button) is an absolutely-positioned
 * fixed corner element, so a shell-level top-left affordance never fights
 * them; it just sits in the ~56px top padding strip every page already
 * reserves (see each page's own .page rule) with room to spare.
 *
 * MOBILE-SHELL (owner's second live mobile-QA ruling, this lane's brief:
 * "we still need some sort of navigation bar or hamburger up for... on top
 * for mobile web"): below 1024px the trigger above rendered as a bare
 * floating circular button with no anchoring bar around it — read back as an
 * icon sitting loose over page content, not as "navigation." .mobileTopBar
 * below is a second, ADDITIVE rendering of the exact SAME trigger (same
 * handleTriggerTap, same SideNavDrawer, zero new state) inside a real fixed
 * header strip (brand slot + hamburger) — CSS-gated to <1024px so desktop
 * keeps today's floating .navTrigger circle exactly as it already renders
 * (brief: "Desktop keeps the current nav exactly as-is"). Both buttons open
 * the identical drawer; only ONE is ever visible at a given width (see
 * AppShell.module.css's own media queries), so there is never a double
 * trigger on screen at once.
 *
 * The trigger is HIDDEN entirely while onboarding is showing (mirrors
 * tabBar's own showTabs=false discipline: `overlay` is present exactly when
 * there is nothing yet to navigate TO — Home doesn't even render at "/" in
 * that state, see App.tsx's homeRouteGate.ts usage) and when onSignOut is
 * unset (no signed-in user to sign out — belt-and-suspenders with the
 * onboarding check, since App.tsx never even mounts AppShell for a signed-out
 * visitor, but a missing onSignOut is itself sufficient reason not to open a
 * drawer whose bottom row would have nothing to call). Both renderings of
 * the trigger share this SAME showTrigger guard.
 */
export function AppShell({ children, tabBar, overlay, coachMark, onSignOut }: AppShellProps) {
  const [navState, setNavState] = useState<SideNavState>('closed');
  const showTrigger = overlay == null && onSignOut != null;
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  // W15 (docs/WALK-FINDINGS-2026-07-21.md) — the Drive-save OAuth round trip
  // (DriveSaveButton.handleConnect) reloads this app fresh on return; AppShell
  // is the ONE wrapper guaranteed to mount on every real route (see this
  // file's own header), and it already owns `.scrollArea` directly here, so
  // this is the natural single place to consume a pending marker regardless
  // of which page/panel happens to remount underneath it. useLayoutEffect
  // (not useEffect) so the restore lands BEFORE first paint — no visible
  // flash at the top before the jump. Mount-only: a marker is single-use by
  // construction (cleared unconditionally below), so this never needs to
  // re-run on a later re-render.
  useLayoutEffect(() => {
    const marker = readDriveScrollMarker();
    const target = resolveScrollRestoreTarget(marker, window.location.pathname, Date.now());
    // Consumed either way (restored or rejected as foreign-path/stale) — an
    // ordinary later reload of the same path must never re-apply it, mirrors
    // sessionHandoff.ts's own finally-strip discipline.
    clearDriveScrollMarker();
    if (target !== null && scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = target;
    }
    // Genuinely empty deps — every symbol read above is either a stable ref
    // or a module-level pure function, never a prop/state value this effect
    // would need to react to again, so this is a complete dependency array,
    // not an intentionally-omitted one.
  }, []);

  function handleTriggerTap() {
    setNavState(toggleSideNav);
  }

  function handleDrawerClose() {
    setNavState(closeSideNav);
  }

  return (
    <div className={styles.page}>
      <div className={styles.desktopFrame}>
        <SideTabRail />
        <div className={styles.shell}>
          {/* MOBILE-SHELL — the fixed top bar, <1024px only (CSS-gated, see
              .mobileTopBar's own media query). Sits ABOVE .scrollArea in
              paint order, same containing block as .navTrigger below it, so
              it never competes with a page's own in-flow header content —
              every page's .page rule already reserves the same 56px this bar
              occupies. */}
          {showTrigger ? (
            <div className={styles.mobileTopBar}>
              <button
                type="button"
                className={styles.mobileTopBarTrigger}
                aria-label="Open menu"
                aria-expanded={navState === 'open'}
                onClick={handleTriggerTap}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          ) : null}
          <div className={styles.scrollArea} ref={scrollAreaRef}>
            {children}
          </div>
          {showTrigger ? (
            <button
              type="button"
              className={styles.navTrigger}
              aria-label="Open menu"
              aria-expanded={navState === 'open'}
              onClick={handleTriggerTap}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 6h16M4 12h16M4 18h16"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
          {onSignOut ? (
            <SideNavDrawer
              open={navState === 'open'}
              onClose={handleDrawerClose}
              onSignOut={onSignOut}
            />
          ) : null}
          {tabBar}
          {overlay}
          {coachMark}
        </div>
      </div>
    </div>
  );
}
